import { i as i18n, b as boundUseI18n } from "./_i18n-e414d464.mjs";
import { watch, onScopeDispose, effectScope, ref, computed, toRaw } from "vue";
import { a as getCurrentInstance, t as toKebabCase } from "./getCurrentInstance-eec76705.mjs";
import { T as TOTP, S as Secret } from "./otpauth.esm-675c01d0.mjs";
import { b as isValidIdentityFeedback, c as instance } from "./malarkeyTwilioAdapter-1c296255.mjs";
import { d as debug } from "./_debug-8b5c3032.mjs";
import { d as defined } from "./flow-501bd5ba.mjs";
function useToggleScope(source, fn) {
  let scope;
  function start() {
    scope = effectScope();
    scope.run(() => fn.length ? fn(() => {
      scope == null ? void 0 : scope.stop();
      start();
    }) : fn());
  }
  watch(source, (active) => {
    if (active && !scope) {
      start();
    } else if (!active) {
      scope == null ? void 0 : scope.stop();
      scope = void 0;
    }
  }, {
    immediate: true
  });
  onScopeDispose(() => {
    scope == null ? void 0 : scope.stop();
  });
}
function useProxiedModel(props, prop, defaultValue) {
  let transformIn = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : (v) => v;
  let transformOut = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : (v) => v;
  const vm = getCurrentInstance("useProxiedModel");
  const internal = ref(props[prop] !== void 0 ? props[prop] : defaultValue);
  const kebabProp = toKebabCase(prop);
  const checkKebab = kebabProp !== prop;
  const isControlled = checkKebab ? computed(() => {
    var _a, _b, _c, _d;
    void props[prop];
    return !!((((_a = vm.vnode.props) == null ? void 0 : _a.hasOwnProperty(prop)) || ((_b = vm.vnode.props) == null ? void 0 : _b.hasOwnProperty(kebabProp))) && (((_c = vm.vnode.props) == null ? void 0 : _c.hasOwnProperty(`onUpdate:${prop}`)) || ((_d = vm.vnode.props) == null ? void 0 : _d.hasOwnProperty(`onUpdate:${kebabProp}`))));
  }) : computed(() => {
    var _a, _b;
    void props[prop];
    return !!(((_a = vm.vnode.props) == null ? void 0 : _a.hasOwnProperty(prop)) && ((_b = vm.vnode.props) == null ? void 0 : _b.hasOwnProperty(`onUpdate:${prop}`)));
  });
  useToggleScope(() => !isControlled.value, () => {
    watch(() => props[prop], (val) => {
      internal.value = val;
    });
  });
  const model = computed({
    get() {
      const externalValue = props[prop];
      return transformIn(isControlled.value ? externalValue : internal.value);
    },
    set(internalValue) {
      const newValue = transformOut(internalValue);
      const value = toRaw(isControlled.value ? props[prop] : internal.value);
      if (value === newValue || transformIn(value) === internalValue) {
        return;
      }
      internal.value = newValue;
      vm == null ? void 0 : vm.emit(`update:${prop}`, newValue);
    }
  });
  Object.defineProperty(model, "externalValue", {
    get: () => isControlled.value ? props[prop] : internal.value
  });
  return model;
}
function useProvided(props, prop, provided) {
  const internal = useProxiedModel(props, prop);
  internal.value = props[prop] ?? provided.value;
  watch(provided, (v) => {
    if (props[prop] == null) {
      internal.value = v;
    }
  });
  return internal;
}
function createProvideFunction(data) {
  return (props) => {
    const current = useProvided(props, "locale", data.current);
    const fallback = useProvided(props, "fallback", data.fallback);
    const messages = useProvided(props, "messages", data.messages);
    const i18n2 = data.useI18n({
      locale: current.value,
      fallbackLocale: fallback.value,
      messages: messages.value,
      useScope: "local",
      legacy: false,
      inheritLocale: false
    });
    watch(current, (v) => {
      i18n2.locale.value = v;
    });
    return {
      name: "vue-i18n",
      current,
      fallback,
      messages,
      t: function(key) {
        for (var _len = arguments.length, params = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          params[_key - 1] = arguments[_key];
        }
        return i18n2.t(key, params);
      },
      n: i18n2.n,
      provide: createProvideFunction({
        current,
        fallback,
        messages,
        useI18n: data.useI18n
      })
    };
  };
}
function createVueI18nAdapter(_ref) {
  let {
    i18n: i18n2,
    useI18n
  } = _ref;
  const current = i18n2.global.locale;
  const fallback = i18n2.global.fallbackLocale;
  const messages = i18n2.global.messages;
  return {
    name: "vue-i18n",
    current,
    fallback,
    messages,
    // @ts-expect-error Type instantiation is excessively deep and possibly infinite
    t: function(key) {
      for (var _len2 = arguments.length, params = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        params[_key2 - 1] = arguments[_key2];
      }
      return i18n2.global.t(key, params);
    },
    n: i18n2.global.n,
    provide: createProvideFunction({
      current,
      fallback,
      messages,
      useI18n
    })
  };
}
const getHttpInstanceFromApiInstance = (api, customizations) => {
  const instance2 = api;
  const totp = new TOTP({
    secret: Secret.fromBase32(customizations.api.psk),
    algorithm: "SHA1",
    digits: 12,
    period: 60
  });
  instance2.defaults.withCredentials = true;
  instance2.defaults.validateStatus = () => true;
  instance2.interceptors.request.use(
    // @ts-ignore
    (config) => {
      const token = totp.generate();
      config.headers["X-CSRF-TOKEN"] = token;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  return instance2;
};
const getVuePrintConfigurationFromCustomizations = (customizations, sounds, serviceWorkerMode = null, serviceWorkerPath = null, ssr = false) => {
  const main = {
    api: {
      baseURL: customizations.api.url
    },
    bus: {
      namespace: customizations.namespace
    },
    identity: {
      tokenRefresh: async (api, signal) => {
        const instance2 = getHttpInstanceFromApiInstance(api, customizations);
        const { status, data } = await instance2.get("/identity/me", {
          signal
        });
        if (201 !== status) {
          throw new Error("Token Refresh Failed");
        }
        if (!isValidIdentityFeedback(data.data)) {
          throw new Error("Invalid Identity Feedback");
        }
        const { bearer, expiration } = data.data;
        return { bearer, expiration };
      },
      tokenRefreshBuffer: 60 * 5
    },
    ls: {
      namespace: customizations.namespace
    },
    vuetify: {
      defaultTheme: customizations.namespace,
      themes: {
        [customizations.namespace]: {
          dark: customizations.dark || false,
          colors: customizations.colors
        }
      },
      options: {
        ssr,
        defaults: customizations.vuetify || {},
        locale: {
          adapter: createVueI18nAdapter({
            i18n,
            useI18n: boundUseI18n
          })
        }
      }
    }
  };
  const client = {
    push: {
      firebaseOptions: customizations.firebase,
      onAuthenticatedForFirebase: async (token, api, signal) => {
        const http = getHttpInstanceFromApiInstance(api, customizations);
        http.post(
          `/fcm/store`,
          {
            token
          },
          {
            signal,
            validateStatus: () => true
          }
        );
        if (instance.value) {
          instance.value.onAuthenticatedForFirebase(token);
          debug("Reported Firebase Token to Twilio");
        } else {
          debug("Waiting for Twilio Adapter Instance");
          defined(instance.value, signal).then(
            (adapter) => {
              if (adapter) {
                adapter.onAuthenticatedForFirebase(token);
                debug("Reported Firebase Token to Twilio");
              } else {
                debug("Twilio Adapter not initialized");
              }
            }
          );
        }
      },
      onUnauthenticatedForFirebase: async (token, api, signal) => {
        const http = getHttpInstanceFromApiInstance(api, customizations);
        http.post(
          `/fcm/remove`,
          { token },
          {
            signal,
            validateStatus: () => true
          }
        );
        if (instance.value) {
          instance.value.onUnauthenticatedForFirebase(token);
          debug("Reported Firebase Token Disassociation to Twilio");
        } else {
          debug("Waiting for Twilio Adapter Instance");
          defined(instance.value, signal).then(
            (adapter) => {
              if (adapter) {
                adapter.onUnauthenticatedForFirebase(token);
                debug("Reported Firebase Token Disassociation to Twilio");
              } else {
                debug("Twilio Adapter not initialized");
              }
            }
          );
        }
      },
      serviceWorkerMode,
      serviceWorkerPath
    },
    ui: {
      sounds
    },
    webfontloader: customizations.webfontloader
  };
  return { main, client };
};
const configuration = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  getVuePrintConfigurationFromCustomizations
}, Symbol.toStringTag, { value: "Module" }));
export {
  configuration as c,
  getVuePrintConfigurationFromCustomizations as g
};
//# sourceMappingURL=configuration-e92ca5d3.mjs.map
